/**
 * @fileoverview Assist field row sub-components extracted from CaseIngestionAssistPanel.
 *
 * Contains: StepContent, FieldSection, FieldRow, StatusIndicator,
 * StepNotesSection, ImportMetadata, and formatDisplayValue helper.
 */
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Info,
  X,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { ImportedAddress } from '@/services/types/case-ingestion-assist-types';

import { CopyButton } from './CopyButton';
import { MultiEntityFieldRow } from './MultiEntityCards';
import type { AssistFieldRow, StepAssistSummary } from './types';

// =============================================================================
// Step Content - Unresolved first, then imported
// =============================================================================

export const StepContent: React.FC<{ summary: StepAssistSummary }> = ({ summary }) => {
  // Exclude notes fields — they are rendered separately via StepNotesSection
  const rows = summary.rows.filter((r) => !r.fieldId.endsWith('_notes'));

  const unresolvedRows = rows.filter(
    (r) => r.status === 'needs_review' || r.status === 'no_import_value',
  );
  const resolvedRows = rows.filter((r) => r.status === 'resolved');
  const naRows = rows.filter((r) => r.status === 'not_applicable');

  return (
    <div className="space-y-3">
      {/* Unresolved items — shown first (attention-first design) */}
      {unresolvedRows.length > 0 && (
        <FieldSection
          title="Needs Attention"
          icon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
          rows={unresolvedRows}
        />
      )}

      {/* Resolved / imported values — always expanded */}
      {resolvedRows.length > 0 && (
        <FieldSection
          title="Imported Values"
          icon={<Check className="h-3 w-3 text-green-500" />}
          rows={resolvedRows}
        />
      )}

      {/* Not applicable — collapsed by default */}
      {naRows.length > 0 && (
        <FieldSection
          title="Not Applicable"
          icon={<X className="h-3 w-3 text-zinc-500" />}
          rows={naRows}
          defaultCollapsed
        />
      )}

      {rows.length === 0 && (
        <p className="text-xs font-medium text-zinc-500">No imported fields for this step.</p>
      )}
    </div>
  );
};

// =============================================================================
// Field Section - Collapsible group of field rows
// =============================================================================

const FieldSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  rows: AssistFieldRow[];
  defaultCollapsed?: boolean;
}> = ({ title, icon, rows, defaultCollapsed = false }) => {
  const [isOpen, setIsOpen] = useState(!defaultCollapsed);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-1.5 text-left">
        {isOpen ? (
          <ChevronDown className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronRight className="h-3 w-3 text-zinc-500" />
        )}
        {icon}
        <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
          {title}
        </span>
        <span className="text-[10px] font-medium text-zinc-500">({rows.length})</span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-1.5 space-y-1 pl-5">
          {rows.map((row) => (
            <FieldRow key={row.fieldId} row={row} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// =============================================================================
// Single Field Row
// =============================================================================

const FieldRow: React.FC<{ row: AssistFieldRow }> = ({ row }) => {
  // Delegate multi-entity fields to card display
  if (row.isMultiEntity && Array.isArray(row.importedValue)) {
    return (
      <div className="rounded-md px-2 py-1">
        <div className="flex items-center gap-1.5">
          <StatusIndicator status={row.status} severity={row.severity} />
          <span className="text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
            {row.label}
          </span>
        </div>
        <div className="mt-1.5 pl-4">
          <MultiEntityFieldRow row={row} />
        </div>
      </div>
    );
  }

  const displayValue = formatDisplayValue(row);
  // Show copy button for scalar values that have actual imported content
  const isCopyable = displayValue != null
    && row.status !== 'no_import_value'
    && row.status !== 'not_applicable';

  return (
    <div className="group flex items-start justify-between gap-2 rounded-md px-2 py-1 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <StatusIndicator status={row.status} severity={row.severity} />
          <span className="text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
            {row.label}
          </span>
        </div>
        {displayValue && (
          <p className="mt-0.5 break-words pl-4 text-[10px] font-medium text-zinc-500">
            {displayValue}
          </p>
        )}
      </div>
      {isCopyable && displayValue && <CopyButton value={displayValue} />}
    </div>
  );
};

// =============================================================================
// Status Indicator
// =============================================================================

const StatusIndicator: React.FC<{
  status: AssistFieldRow['status'];
  severity: AssistFieldRow['severity'];
}> = ({ status, severity }) => {
  if (status === 'resolved') {
    return <Check className="h-3 w-3 text-green-500" />;
  }
  if (status === 'needs_review') {
    return (
      <AlertTriangle
        className={cn(
          'h-3 w-3',
          severity === 'high' ? 'text-amber-500' : 'text-zinc-500',
        )}
      />
    );
  }
  if (status === 'no_import_value') {
    return <Info className="h-3 w-3 text-zinc-500" />;
  }
  // not_applicable
  return <X className="h-3 w-3 text-zinc-500" />;
};

// =============================================================================
// Step Notes - Prominent display for step-level notes (liability, treatment, coverage)
// =============================================================================

export const StepNotesSection: React.FC<{
  row: AssistFieldRow;
  defaultOpen?: boolean;
}> = ({ row, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const notesText = typeof row.importedValue === 'string' ? row.importedValue : null;
  if (!notesText) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="mt-3 flex w-full items-center gap-1.5 text-left">
        {isOpen ? (
          <ChevronDown className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronRight className="h-3 w-3 text-zinc-500" />
        )}
        <FileText className="h-3 w-3 text-zinc-500" />
        <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
          {row.label}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="group mt-1.5 rounded-md bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <div className="flex items-start justify-between gap-1">
            <p className="min-w-0 flex-1 whitespace-pre-wrap text-[11px] font-medium text-zinc-600 dark:text-zinc-500">
              {notesText}
            </p>
            <CopyButton value={notesText} />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// =============================================================================
// Consolidated Notes - All notes on the final Notes step with Copy All
// =============================================================================

const NOTES_FIELD_IDS: { stepId: string; fieldId: string; label: string }[] = [
  { stepId: 'liability', fieldId: 'liability.liability_notes', label: 'Liability Notes' },
  { stepId: 'treatment', fieldId: 'treatment.treatment_notes', label: 'Treatment Notes' },
  { stepId: 'coverage', fieldId: 'coverage.coverage_notes', label: 'Coverage Notes' },
];

export const ConsolidatedNotesSection: React.FC<{
  stepSummaries: Record<string, StepAssistSummary>;
  sourceNotes: string | null;
}> = ({ stepSummaries, sourceNotes }) => {
  const [isOpen, setIsOpen] = useState(true);

  const noteEntries: { label: string; text: string }[] = NOTES_FIELD_IDS
    .map(({ stepId, fieldId, label }) => {
      const stepSummary = stepSummaries[stepId];
      if (!stepSummary) return null;
      const row = stepSummary.rows.find((r) => r.fieldId === fieldId);
      if (row && typeof row.importedValue === 'string' && row.importedValue) {
        return { label, text: row.importedValue };
      }
      return null;
    })
    .filter((entry): entry is { label: string; text: string } => entry !== null);

  if (sourceNotes) {
    noteEntries.push({ label: 'Source Notes', text: sourceNotes });
  }

  const consolidatedText = noteEntries
    .map(({ label, text }) => `--- ${label} ---\n${text}`)
    .join('\n\n');

  const handleCopyAll = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(consolidatedText);
      toast.success('All notes copied to clipboard');
    } catch {
      toast.error('Failed to copy notes');
    }
  }, [consolidatedText]);

  if (noteEntries.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="mt-3 flex w-full items-center gap-1.5 text-left">
        {isOpen ? (
          <ChevronDown className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronRight className="h-3 w-3 text-zinc-500" />
        )}
        <FileText className="h-3 w-3 text-zinc-500" />
        <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
          All Notes
        </span>
        <span className="text-[10px] font-medium text-zinc-500">({noteEntries.length})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 space-y-2">
          {noteEntries.map(({ label, text }) => (
            <div key={label} className="group rounded-md bg-zinc-50 p-2 dark:bg-zinc-800/50">
              <h5 className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {label}
              </h5>
              <div className="mt-1 flex items-start justify-between gap-1">
                <p className="min-w-0 flex-1 whitespace-pre-wrap text-[11px] font-medium text-zinc-600 dark:text-zinc-500">
                  {text}
                </p>
                <CopyButton value={text} />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleCopyAll}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
          >
            <FileText className="h-3 w-3" />
            Copy All Notes
          </button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// =============================================================================
// Import Metadata
// =============================================================================

export const ImportMetadata: React.FC<{
  importedAt: string | null;
}> = ({ importedAt }) => {
  if (!importedAt) return null;

  const formattedDate = new Date(importedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="mt-3 flex items-center gap-3 border-t border-zinc-100 pt-2 dark:border-zinc-800">
      <span className="flex items-center gap-1 text-[10px] font-medium text-zinc-500">
        <Clock className="h-2.5 w-2.5" />
        Imported {formattedDate}
      </span>
    </div>
  );
};

// =============================================================================
// All Fields Overview - Catch-all for the Notes (case_summary) step
// =============================================================================

export const AllFieldsOverview: React.FC<{
  stepSummaries: Record<string, StepAssistSummary>;
}> = ({ stepSummaries }) => {
  const [isOpen, setIsOpen] = useState(false);

  const steps = Object.values(stepSummaries).filter((s) => s.rows.length > 0);
  if (steps.length === 0) return null;

  const totalCount = steps.reduce((sum, s) => sum + s.rows.length, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="mt-3 flex w-full items-center gap-1.5 text-left">
        {isOpen ? (
          <ChevronDown className="h-3 w-3 text-zinc-500" />
        ) : (
          <ChevronRight className="h-3 w-3 text-zinc-500" />
        )}
        <Info className="h-3 w-3 text-zinc-500" />
        <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300">
          All Imported Fields
        </span>
        <span className="text-[10px] font-medium text-zinc-500">({totalCount})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 space-y-3 pl-4">
          {steps.map((step) => (
            <div key={step.stepId}>
              <h4 className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {step.stepLabel}
              </h4>
              <div className="mt-1">
                <StepContent summary={step} />
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// =============================================================================
// Helpers
// =============================================================================

function formatDisplayValue(row: AssistFieldRow): string | null {
  if (row.status === 'no_import_value') return 'No imported value found';
  if (row.status === 'not_applicable') return null;

  const formatValue = (value: unknown): string => {
    // Handle structured address objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'line1' in value) {
      return formatImportedAddress(value as ImportedAddress);
    }
    return String(value);
  };

  // For resolved fields, show just the value
  if (row.status === 'resolved' && row.importedValue != null) {
    if (row.isMultiEntity && Array.isArray(row.importedValue)) {
      return `${row.importedValue.length} item${row.importedValue.length !== 1 ? 's' : ''} imported`;
    }
    return formatValue(row.importedValue);
  }

  // For needs_review, show just the value (status indicator handles the visual)
  if (row.status === 'needs_review' && row.importedValue != null) {
    if (row.isMultiEntity && Array.isArray(row.importedValue)) {
      return `${row.importedValue.length} item${row.importedValue.length !== 1 ? 's' : ''}`;
    }
    return formatValue(row.importedValue);
  }

  return null;
}

/** Format a structured address as a readable one-line string. */
function formatImportedAddress(addr: ImportedAddress): string {
  const parts: string[] = [addr.line1];
  if (addr.line2) parts.push(addr.line2);
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  if (cityState && addr.postcode) {
    parts.push(`${cityState} ${addr.postcode}`);
  } else if (cityState) {
    parts.push(cityState);
  } else if (addr.postcode) {
    parts.push(addr.postcode);
  }
  return parts.join(', ');
}
